import { compose, type Dispatch } from 'redux';

export type Action<Type extends string, Meta, Payload> = {
  type: Type;
  meta: Meta;
  payload: Payload;
};
export type BoundActionCreator<Type extends string, Meta, Payload> = Payload extends undefined
  ? () => Action<Type, Meta, Payload>
  : (payload: Payload) => Action<Type, Meta, Payload>;

export type UnboundActionCreator<Type extends string, Meta, Payload> = {
  (meta: Meta): BoundActionCreator<Type, Meta, Payload>;
  toString(): Type;
};

export type BoundActionType<T extends UnboundActionCreator<any, any, any>> = ReturnType<ReturnType<T>>;

export function createBoundAction<Type extends string>(
  type: Type
): <Meta, Payload = undefined>() => UnboundActionCreator<Type, Meta, Payload> {
  return <Meta, Payload>(): UnboundActionCreator<Type, Meta, Payload> => {
    const creator =
      (meta: Meta) =>
      (payload: Payload): Action<Type, Meta, Payload> => ({
        type,
        meta,
        payload
      });
    creator.toString = (): string => type;
    // Cast because there is no difference between action() and action(undefined)
    // Typescript cares so we need to reassure it it's ok
    return creator as UnboundActionCreator<Type, Meta, Payload>;
  };
}

export function bindAction<Type extends string, Meta, Payload>(
  action: UnboundActionCreator<Type, Meta, Payload>,
  meta: Meta,
  dispatch?: Dispatch
): BoundActionCreator<Type, Meta, Payload> {
  const bindParams = (f: UnboundActionCreator<Type, Meta, Payload>): BoundActionCreator<Type, Meta, Payload> => f(meta);
  let bind = bindParams;

  if (dispatch) {
    bind = (f: UnboundActionCreator<Type, Meta, Payload>): BoundActionCreator<Type, Meta, Payload> =>
      // Cast because there is no difference between action() and action(undefined)
      // Typescript cares so we need to reassure it it's ok
      compose(dispatch, bindParams(f)) as BoundActionCreator<Type, Meta, Payload>;
  }

  return bind(action);
}
